/**
 * 送付状（印刷・FAX）の1回目（#223、docs/89。2026-10-20 決定・10/21 追加 ★7・★8）。
 *   ・テンプレートは1種類（会社で複数・既定1つ）。型（FAX／窓付封筒）は出力のとき選ぶ。題と挨拶は型ごとにテンプレートが持つ
 *   ・挨拶は3択（なし／簡潔／丁寧）。丁寧なら本文の後に「敬具」。記 … 以上 はいつも
 *   ・**送付状に金額を出さない**（★4）：差し込みに金額は無い・記の行にも金額の列は無い（この型にも無い）
 *   ・窓付封筒は送付状があるときだけ（★8）。窓に出すのは送付状の宛名だけ＝同封書類の紙（A4 横・自分の書式）は変わらない
 *   ・折り目の印（★7）：窓付封筒の送付状の1枚目だけ。上から 99mm・198mm の左右の端に墨の三角（左「▶」・右「◀」）
 *   ・印刷・PDF の保存の瞬間に送付の記録（document_deliveries）へ固める。テンプレートを直しても出した送付状は変わらない
 * 紙の描画は packages/paper の renderCoverLetter（API の PDF と設定の見本が同じ関数）。画面の文はここ（COVER_LETTER_MSG）に1か所
 */
import { z } from "zod";
import { formatPhone } from "./phone.js";

/* ============================================================================
 * 決まり（DB の CHECK・既定と同じ値）
 * ========================================================================== */

/** 挨拶：なし／簡潔／丁寧（docs/89 §0.1 ★5） */
export const CoverGreeting = z.enum(["none", "brief", "formal"]);
export type CoverGreeting = z.infer<typeof CoverGreeting>;
/** 型：FAX（既定）／窓付封筒（出力のときのスイッチ） */
export const CoverMode = z.enum(["fax", "window"]);
export type CoverMode = z.infer<typeof CoverMode>;

/** 紙に出る挨拶の文（丁寧のときは本文の後に右寄せで「敬具」） */
export const COVER_GREETINGS: Record<CoverGreeting, string> = {
  none: "",
  brief: "いつもお世話になっております。",
  formal: "拝啓　時下ますますご清栄のこととお慶び申し上げます。平素は格別のお引き立てを賜り厚く御礼申し上げます。",
};
export const COVER_GREETING_LABEL: Record<CoverGreeting, string> = { none: "なし", brief: "簡潔", formal: "丁寧" };

/** 長さ・数の上限（DB の cover_letter_shape・document_delivery_items と同じ） */
export const COVER_LETTER_LIMITS = {
  name: 40,
  title: 30,
  body: 1000,
  remarks: 300,
  subject: 100,
  manualLines: 10,
  lineLabel: 60,
  copies: 99,
  pages: 999,
  fax: 20,
  /** 未登録の送付先（3回目の前の直し＝★13、docs/89 §17.3）：会社名・部署・担当者・住所の長さ（{customer}の登録と同じ） */
  recipientName: 200,
  recipientDepartment: 50,
  recipientContact: 100,
  recipientAddress: 200,
  /** 会社に置けるテンプレートの数 */
  templates: 20,
} as const;

/**
 * 束ね（2回目、docs/89 §6）の上限：1回の送付に同封できる書類の数・本状含めのページ数・同封書類の合計の大きさ。
 *   ページ数 50＝複合機の原稿の送り（ADF）の1回分（キヤノン Satera MF269dw II などの小型の複合機で最大 50 枚。docs/89 §16.9）。
 *   書類 20＝見積依頼の添付と同じ数（記の行は手の行 10 と合わせて 30 まで＝送付状が2枚までで収まる）。
 *   大きさ 20MB＝受け取った書類1つの上限（RECEIVED_MAX_BYTES）と同じ。画像は長辺 imagePx（約 200dpi の A4）まで縮めて入れる
 */
export const COVER_BUNDLE_LIMITS = { items: 20, pages: 50, bytes: 20 * 1024 * 1024, imagePx: 2400, imageMarginMm: 10 } as const;

/** テンプレートの既定（DB の列の既定・会社を作ったときの「標準」と同じ） */
export const COVER_LETTER_DEFAULTS = {
  name: "標準",
  titleFax: "FAX送信のご案内",
  titleWindow: "書類送付のご案内",
  greetingFax: "brief" as CoverGreeting,
  greetingWindow: "formal" as CoverGreeting,
  body: "下記の書類をお送りいたします。ご査収のほど、よろしくお願い申し上げます。",
  remarks: "",
  printAddress: true,
  printRepresentative: false,
  printStaff: true,
  printStaffEmail: false,
} as const;

/*
 * 窓付封筒（docs/89 §3.2・§4.1。2026-10-21 射場さんの改め＝封筒の袋や商品ページにある「窓の位置と大きさ」をそのまま入れる）
 *   ・封筒は宛名を読む向き（横長。長3 は 235×120mm・口が右・底（奥）が左）。窓は封筒の左の端・上の端から窓の端までの mm
 *   ・紙の上の宛名の枠はシステムが計算する（packages/paper の coverAddressLayout。設定の図・見本・出力の PDF が同じ値）：
 *     A4 を三つ折りにして上の段を表に、封筒の底まで入れ、上の辺にそろえた位置（COVER_PAPER_IN_ENVELOPE）を前提に、
 *     窓の内側に余白（COVER_ADDRESS_MARGIN_MM）をとり、宛名の微調整（上下・左右）を足す
 */
/** 封筒の種類（今は長形3号だけ）。w・h は宛名を読む向き（横長）の外形 mm */
export const COVER_ENVELOPE_KINDS = { naga3: { label: "長形3号", w: 235, h: 120 } } as const;
export type CoverEnvelopeKind = keyof typeof COVER_ENVELOPE_KINDS;
/**
 * 既定（「既定に戻す」）：市販の長3窓付で多い値＝左端から 24・上端から 12・幅 90・高さ 45mm（2026-10-21 調べ。
 * 東京封筒・封筒屋どっとこむ・JIM封筒が同じ値、ヤマガ印刷は「上端から 12・左端から 20〜24」。モノタロウの一部の商品は 左 23・上 13。docs/89 §4.1）
 */
export const COVER_ENVELOPE_DEFAULT = { kind: "naga3", x: 24, y: 12, w: 90, h: 45, dx: 0, dy: 0 } as const;
/** 欄の範囲（DB の fn_cover_letter_settings_ok と同じ）：窓は封筒の中・幅 30〜200・高さ 20〜100mm、微調整は ±20mm、どれも 0.5mm 刻み */
export const COVER_ENVELOPE_RANGE = { w: [30, 200], h: [20, 100], shift: 20, step: 0.5 } as const;
/**
 * 封筒の中の紙（三つ折りの上の段 210×99mm）の左上（封筒の左上から mm）：底（奥＝横長の左）に当て、上の辺にそろえた位置。
 * 実際の紙は封筒の中で動く（封筒の縦 120mm に紙 99mm）ので、宛名の枠は窓の内側に余白をとり、ずれは宛名の微調整で直す（docs/89 §4.1 の図）
 */
export const COVER_PAPER_IN_ENVELOPE = { x: 0, y: 0, w: 210, h: 99 } as const;
/** 宛名の枠を窓の内側に置く余白（mm。設定には出さない） */
export const COVER_ADDRESS_MARGIN_MM = 3;
/** 宛名の文字（pt）：ふだん 10.5、枠に入らなければ 0.5 刻みで 9 まで縮める。会社名は 12pt（同じ割合で縮める）。行の高さは 1.5 倍 */
export const COVER_ADDRESS_PT = { max: 10.5, min: 9, step: 0.5, company: 12, lineHeight: 1.5 } as const;
/** プリンタの刷れない余白（折り目の印と同じ 6mm）。宛名の枠はこの内側（上・左・右）、下は三つ折りの線（99mm）まで */
export const PRINT_SAFE_MM = 6;
/** 設定の図・見本の宛名（〒・住所2行・会社名・支店・部署 氏名 様＝6行。収まるかを見るのはこの宛名） */
export const COVER_SAMPLE_RECIPIENT = { name: "株式会社見本", branch: "大阪支店", contact: "工事部 山田 太郎", postalCode: "5300001", address: "大阪府大阪市北区梅田1-1-1\n見本ビル 5階", fax: "06-1234-5678" } as const;

/**
 * 折り目の印（★7、docs/89 §3.2）：上から 99mm・198mm（A4 縦の 1/3・2/3）の左右の端。一辺 3mm の正三角形（高さ 2.6mm）、墨の塗りつぶし。
 * 底辺（外側の辺）は紙の端から 6mm（プリンタの刷れない余白：キヤノンのレーザー 4.2mm・Satera の説明書 5mm・国税庁の推奨環境 6mm の内側）
 */
export const FOLD_MARKS = { ysMm: [99, 198], insetMm: 6, sideMm: 3, heightMm: 2.6 } as const;

/** 通し番号（FAX の型）：全ページの下の中央「n／N　書類名 番号」。N は「本状含め」の枚数（手の行の枚数も数える）。9pt の墨 */
export const THROUGH_NUMBER = { sizePt: 9 } as const;
/**
 * 通し番号の帯（mm）：ページの下の中央・幅 140・高さ 6・紙の下端から 5mm（帯は下端から 5〜11mm、字の中心は下から 8mm）。
 * 自分の書式（帳票デザイン）の紙にも重ねる（docs/89 §15 の7）＝エディタはこの帯に部品が重なると注意を出す（packages/paper の layoutWarningItems）
 */
export const THROUGH_BAND_MM = { w: 140, h: 6, bottom: 5 } as const;
/** 紙（w×h mm）の上の通し番号の帯（紙の左上から mm） */
export const throughBandBox = (page: { w: number; h: number }): { x: number; y: number; w: number; h: number } => ({
  x: (page.w - THROUGH_BAND_MM.w) / 2,
  y: page.h - THROUGH_BAND_MM.bottom - THROUGH_BAND_MM.h,
  w: THROUGH_BAND_MM.w,
  h: THROUGH_BAND_MM.h,
});
export const THROUGH_BAND_NAME = "通し番号（FAX）";

/** 差し込み（本文と備考だけ）。見積依頼のメールと同じ全角の角括弧。**金額は無い**（★4） */
export const COVER_FIELDS = ["［宛名］", "［件名］", "［書類］", "［自社名］", "［担当者］"] as const;
export type CoverFieldValues = { to: string; subject: string; docs: string; company: string; staff: string };
export function fillCover(text: string, v: CoverFieldValues): string {
  return text.replaceAll("［宛名］", v.to).replaceAll("［件名］", v.subject).replaceAll("［書類］", v.docs).replaceAll("［自社名］", v.company).replaceAll("［担当者］", v.staff);
}

/** 帳票の種類 → 記の書類名（帳票が増えたらここに1行。docs/89 §9） */
export const COVER_DOC_LABEL = {
  quote: "見積書",
  invoice: "請求書",
  receipt: "領収書",
  rfq: "見積依頼書",
  /** {order}（#233）は会社の言葉の帳票の名前（API は Order.names.order を使う。ここは言葉のキー） */
  order: "{order}",
  /** {paymentNotice}（#233 3b）も会社の言葉の帳票の名前（API は PaymentNotice.names.paymentNotice を使う） */
  payment_notice: "{paymentNotice}",
  /** {deliveryNote}（#266）も会社の言葉の帳票の名前（API は DeliveryNote.names.deliveryNote を使う） */
  delivery_note: "{deliveryNote}",
  /** 検収書（#266 1b） */
  acceptance: "{acceptance}",
  photoLedger: "写真台帳",
  /** 受け取った書類・その場で選ぶ PDF の書類名が空のとき（ふだんは書類の名前・ファイル名） */
  file: "添付書類",
} as const;
export type CoverDocKind = "quote" | "invoice" | "receipt" | "rfq" | "order" | "payment_notice" | "delivery_note" | "acceptance";

/** 記の1行の文字：「見積書（No. Q-0012）」（送付状の紙と送付状の枠の記の行） */
export const coverLineText = (l: { label: string; docNo: string | null }): string => (l.docNo ? `${l.label}（No. ${l.docNo}）` : l.label);
/** 送付の記録の一覧の同封書類の1件：「見積書 Q-0012」（#313：「（No. …）」は付けない。紙の記の行は coverLineText のまま） */
export const deliveryItemText = (l: { label: string; docNo: string | null }): string => (l.docNo ? `${l.label} ${l.docNo}` : l.label);
/** 送付の記録の一覧の2行目：「同封　見積書 Q-0003・請求書 I-0012」（同封書類が無ければ空） */
export const deliveryItemsText = (items: ReadonlyArray<{ label: string; docNo: string | null }>): string =>
  items.length ? `${COVER_LETTER_MSG.itemsHead}　${items.map(deliveryItemText).join("・")}` : "";
/** 通し番号の文字：「2／3　見積書 Q-0012」（送付状のページは「1／3　送付状」） */
export const throughNumberText = (n: number, total: number, label: string, docNo: string | null): string => `${n}／${total}　${throughLabel(label)}${docNo ? ` ${docNo}` : ""}`;
/** 通し番号の帯（幅 140mm・9pt）に入る長さの書類名（受け取った書類の名前は 60 字まであるので、26 字を超えたら切って「…」） */
export const throughLabel = (label: string): string => ([...label].length > 26 ? `${[...label].slice(0, 25).join("")}…` : label);
/** 「本状含め n枚」の n：出す PDF のページ数＋（FAX のとき）手の行に打った枚数（docs/89 §15 の5） */
export function coverTotalPages(pdfPages: number, manualLines: ReadonlyArray<{ pages: number | null }>, mode: CoverMode): number {
  return pdfPages + (mode === "fax" ? manualLines.reduce((a, l) => a + (l.pages ?? 0), 0) : 0);
}
/** 郵便番号 7桁 → 「〒660-0805」（形の違うものはそのまま） */
export const zipText = (zip: string): string => {
  const d = zip.replace(/[^0-9]/g, "");
  return d.length === 7 ? `〒${d.slice(0, 3)}-${d.slice(3)}` : zip ? `〒${zip}` : "";
};

/* ============================================================================
 * 文（画面・紙）
 * ========================================================================== */

export const COVER_LETTER_MSG = {
  // 設定 → 送付状
  settingsTitle: "送付状",
  settingsHelp: "印刷・FAX で書類を送るときの1枚目です。テンプレートは本文まで持ち、出力のときに FAX の型か窓付封筒の型を選びます。送付状に金額は出ません。",
  sectionTemplates: "テンプレート",
  addTemplate: "テンプレート追加",
  defaultMark: "既定",
  sectionDefault: "既定",
  printDefault: "印刷に添付",
  mailDefault: "メールに添付",
  printDefaultHelp: "帳票の送信の画面で、送付状を付けた形で始めます（印刷・メールそれぞれ）。{customer}の 送信設定 の「送付状」が先です。出力のたびに外せます。",
  sectionWindow: "窓付封筒",
  windowHelp: "送付状の宛名を封筒の窓に合わせます。",
  // 設定 → 送付状 → 窓付封筒（/settings/cover-letters/envelope）
  envelopeTitle: "窓付封筒",
  envelopeHelp:
    "封筒の袋や商品ページにある窓の位置と大きさを入力します。封筒は宛名を読む向き（横長・口が右）で、封筒の端から窓の端までの mm です。宛名を刷る位置はシステムが計算します（A4 を三つ折りにして上の段を表に、封筒の底まで入れた位置）。既定に戻す は市販の長形3号の窓付封筒で多い値（左端から 24・上端から 12・幅 90・高さ 45mm）に戻し、宛名の微調整は 0 に戻ります。",
  envelopeFigure: "窓付封筒の図",
  envelopeKind: "封筒",
  sectionEnvelopeWindow: "窓",
  envelopeX: "左端から",
  envelopeY: "上端から",
  envelopeW: "幅",
  envelopeH: "高さ",
  sectionEnvelopeShift: "宛名の微調整",
  envelopeShiftHelp: "位置合わせシートを刷って封筒に入れ、窓の点線が封筒の窓の縁とずれていたら、点線を窓の縁に重ねる向きに、ずれた分を入力します。上下は＋で下へ、左右は＋で右へ動きます。",
  envelopeDY: "上下",
  envelopeDX: "左右",
  envelopeReset: "既定に戻す",
  envelopeResetDone: "既定に戻しました。",
  envelopeNotFit: "宛名が窓に収まりません",
  envelopeOutside: "窓は封筒（横 235mm・縦 120mm）の中に入力してください",
  envelopeSize: "窓は 幅 30〜200mm・高さ 20〜100mm です",
  envelopeShiftRange: "宛名の微調整は ±20mm までです",
  envelopeStep: "0.5mm 刻みで入力してください",
  envelopeRequired: "数値を入力してください",
  figPaper: "三つ折りの上の段（210×99mm）",
  figAddress: "宛名",
  figMouth: "口",
  figBottom: "底",
  /** 見本・出力の枠：宛名の文字を 9pt まで縮めても枠に入らない（それ以上は縮めない） */
  addressTooLong: "宛名が長く、窓に入りきりません",
  alignmentSheet: "位置合わせシート",
  // テンプレート
  templateTitle: "テンプレート",
  templateName: "テンプレート名",
  newTemplateName: "新しいテンプレート",
  sectionFax: "FAX",
  sectionWindowType: "窓付封筒",
  title: "題",
  greeting: "挨拶",
  greetingHelp: "簡潔：いつもお世話になっております。　丁寧：拝啓…（本文の後に「敬具」）",
  sectionBody: "本文",
  body: "本文",
  remarks: "備考",
  fieldsHelp: "差し込み：［宛名］［件名］［書類］［自社名］［担当者］。金額は差し込めません。",
  sectionPrint: "印字項目",
  printAddress: "自社の住所",
  printRepresentative: "代表者名",
  printStaff: "担当",
  printStaffEmail: "担当のメール",
  preview: "見本",
  setDefault: "既定に設定",
  setDefaultDone: "「{name}」を既定にしました。",
  delete: "削除",
  deleteAsk: "「{name}」を削除しますか。出した送付状は変わりません。",
  deleted: "「{name}」を削除しました。",
  defaultCannotDelete: "既定のテンプレートは削除できません。先にほかのテンプレートを既定にしてください。",
  // 出力の枠（帳票の紙の画面・見積依頼の送付・単体送付状）
  panelTitle: "送付状",
  coverSwitch: "送付状",
  windowSwitch: "窓付封筒",
  template: "テンプレート",
  to: "送付先",
  toFax: "送信先 FAX",
  toFaxMissing: "FAX 番号が未登録です",
  toFaxHelp: "送信先の FAX 番号は 支店 → {customer} の順に登録した番号が入ります。この欄に入力すると、この送付状に出ます（登録した番号は変わりません）。",
  saveFaxToPartner: "{customer}に登録",
  date: "日付",
  subject: "件名",
  lines: "記",
  addLine: "＋ 行追加",
  lineLabel: "書類名",
  copies: "部数",
  pages: "枚数",
  copiesUnit: "部",
  removeLine: "行削除",
  totalPages: "本状含め {n}枚",
  print: "印刷",
  save: "PDF 保存",
  making: "送付状作成中…",
  panelOpen: "送付状表示",
  panelClose: "送付状を閉じる",
  // 単体送付状
  newTitle: "送付状",
  create: "送付状作成",
  pickPartner: "送付先選択",
  // 左のメニュー「送付状」＝送付の記録の一覧（会社の全件。3回目の前の直し＝★12、docs/89 §17.3）
  menu: "送付状",
  listTitle: "送付状",
  listHelp: "印刷・PDF の保存・メールで送付状を出した記録です（会社の全件・新しい順）。「表示」で、そのとき保存した PDF を開きます（作り直しません）。記録は修正・削除できません。",
  listMore: "さらに表示",
  // 送付先の選び方（{customer}／{vendor}／未登録。★13）
  recipientKind: "送付先",
  kindUnregistered: "未登録",
  recipientName: "会社名",
  recipientDepartment: "部署",
  recipientContact: "担当者",
  recipientPostal: "郵便番号",
  recipientAddress: "住所",
  recipientNameRequired: "会社名を入力してください",
  recipientPostalInvalid: "郵便番号は7桁で入力してください",
  recipientHelp: "登録していない送付先です。入力した内容はこの送付の記録にだけ残ります（部署・担当者・FAX・郵便番号・住所は暗号化して保存）。帳票は同封できません（PDF ファイルは同封できます）。",
  registerPartner: "{customer}に登録",
  registerPartnerHelp: "チェックを入れると、印刷・PDF の保存のときに{customer}として登録してから送付します（送付の記録は登録した{customer}に付きます）。",
  registered: "{customer}「{name}」を登録しました。",
  unregisteredDoc: "未登録の送付先には帳票を同封できません",
  unregisteredMark: "未登録",
  // 送付の記録の PDF の画面（/deliveries/:id。★18）
  pdfPageTitle: "送付状",
  download: "ダウンロード",
  // 紙（PDF）
  paperTo: "送信先",
  paperFrom: "送信元",
  paperSubject: "件名：",
  paperPages: "本状含め {n}枚",
  paperKi: "記",
  paperIjou: "以上",
  paperKeigu: "敬具",
  paperRemarks: "備考：",
  paperStaff: "担当",
  paperCoverLabel: "送付状",
  alignTitle: "位置合わせシート",
  alignNote: "拡大・縮小せずに印刷してください（倍率 100%）。",
  alignSteps: [
    "この紙を三つ折り（上から 99mm・198mm の三角の印）にし、上の段を表にして窓付封筒の底まで入れます。",
    "点線（窓）が封筒の窓の縁と重なっているかを見ます。実線は宛名を刷る枠です。",
    "ずれていたら、点線を窓の縁に重ねる向きに、ずれた分を 宛名の微調整 で修正します（上下は＋で下へ・左右は＋で右へ）。",
  ],
  alignWindow: "窓（左端から {x}・上端から {y}・幅 {w}・高さ {h} mm）",
  alignFrame: "宛名の枠",
  alignShift: "宛名の微調整（上下 {dy}・左右 {dx} mm）",
  alignFold: "三つ折り",
  // 束ね（2回目、docs/89 §6）
  addDocs: "書類追加",
  addSameRecipient: "書類追加",
  pickerTitle: "書類追加（同じ送付先）",
  pickerHelp: "同じ送付先（{customer}・支店・送信先 FAX が同じ）の発行した帳票を、{project}をまたいで同封できます。受け取った書類（図面・PDF・画像）と、その場で選ぶ PDF も同封できます。画像は A4 縦の1ページに収めます。",
  pickerTo: "送付先",
  tabDocs: "帳票",
  tabReceived: "受け取った書類",
  tabFile: "PDF ファイル",
  pickerGroup: "{project}",
  pickerEmptyDocs: "同じ送付先の発行した帳票はありません",
  pickerEmptyReceived: "受け取った書類はありません",
  pickerEmptyGroups: "受け取った書類のある{project}はありません",
  pickerFileHelp: "PDF を選択します（20MB・50ページまで）。送付の記録にだけ残り、{project}の受け取った書類には入りません。",
  pickFile: "ファイル選択",
  uploading: "PDF 読み込み中…",
  loading: "読み込み中…",
  included: "同封済",
  pickerAdd: "書類追加",
  pickerSelected: "{n}件を選択",
  bundleTitle: "同封書類",
  moveUp: "上へ",
  moveDown: "下へ",
  removeItem: "削除",
  itemLabel: "書類名",
  pagesShort: "{n}ページ",
  branch: "支店・営業所",
  branchNone: "指定なし",
  bundleTooMany: "同封できる書類は {n}件までです",
  bundleTooManyPages: "本状含め {n}ページまでです（現在 {m}ページ）",
  bundleTooBig: "同封書類は合計 {n}MB までです（現在 {m}MB）",
  recipientMixed: "送付先が違う書類は同封できません",
  itemDup: "同じ書類が2回入っています",
  fileType: "PDF ファイルを選択してください",
  fileBroken: "PDF を読み込めません",
  fileEncrypted: "パスワード付きの PDF は同封できません",
  fileTooBig: "PDF は 20MB までです",
  fileTooManyPages: "PDF は 50ページまでです",
  fileNotFound: "PDF が見つかりません",
  receivedNotFound: "受け取った書類が見つかりません",
  receivedText: "文字の書類は同封できません",
  // 送付の記録の一覧（2回目）
  deliveriesTitle: "送付の記録",
  deliveriesTab: "送付",
  deliveriesCount: "送付 {n}回",
  deliveriesEmpty: "送付の記録はありません",
  /** #313（11/5）：送付状つきの印刷・PDF 保存・メール送信・手動の送付済の記録 */
  deliveriesHelp: "送付状つきの印刷・PDF 保存、メール送信、手動の送付済を記録します。「表示」で送付時の PDF を開きます。記録は修正・削除できません。",
  colDate: "日時",
  /** #313：送付先と同封書類は1列（2行目に「同封　見積書 Q-0003」） */
  colTo: "送付先・同封書類",
  colMode: "送付方法",
  colPages: "枚数",
  colBy: "送付者",
  /** 一覧の2行目の頭（「同封　見積書 Q-0003」） */
  itemsHead: "同封",
  /** 送付の記録の窓の右上の件数 */
  itemsCount: "{n}件",
  modeFax: "FAX",
  /** #313（★4）：一覧・札・送付の記録の PDF の画面の帯は「窓付」（設定の画面名とスイッチは「窓付封筒」のまま＝windowSwitch） */
  modeWindow: "窓付",
  modeMail: "メール",
  /** #317：送付状なしの印刷・PDF の保存・リンク（経路が LINE・SMS ならその名前） */
  modePrint: "印刷",
  modeDownload: "PDF 保存",
  modeLink: "リンク",
  modeLine: "LINE",
  modeSms: "SMS",
  /** 一覧の ⋯ で手で切り替えた送付（#304）。#313：札「手動」と、その右の札「送付済」「未送付に戻す」 */
  modeManual: "手動",
  manualSent: "送付済",
  manualUnsent: "未送付に戻す",
  pagesUnit: "{n}枚",
  openPdf: "PDF 表示",
  // {customer}ごとの既定（2回目）
  partnerSection: "送信設定",
  partnerHelp: "この{customer}に書類を送るときの初めの値です。発行したあとの送信の画面がこの送信方法で開き、送付状は「会社に従う」なら 設定 → 送付状 の「印刷に添付」「メールに添付」のとおりに始めます。テンプレートと窓付封筒は送付状の枠と単体送付状で使います。どれも送信のたびに変えられます。窓付封筒をオンにすると、印刷では送付状を付けた形で始めます。",
  partnerSendMethod: "送信方法",
  partnerAttach: "送付状",
  partnerAttachCompany: "会社に従う（{state}）",
  partnerAttachOn: "添付する",
  partnerAttachOff: "添付しない",
  /** 会社に従うの中の会社の既定（印刷：添付・メール：なし） */
  partnerAttachState: (print: boolean, mail: boolean) => `印刷：${print ? "添付" : "なし"}・メール：${mail ? "添付" : "なし"}`,
  partnerTemplate: "テンプレート",
  partnerTemplateCompany: "会社の既定（{name}）",
  partnerWindow: "窓付封筒",
  partnerSaved: "送信設定を保存しました。",
  // 断り
  notFound: "送付状のテンプレートが見つかりません",
  templateLimit: "テンプレートは {n} 個までです",
  nameDup: "同じ名前のテンプレートがあります",
  nameRequired: "テンプレート名を入力してください",
  titleRequired: "題を入力してください",
  docNotIssued: "送付状を付けられるのは発行した帳票だけです",
  docNotFound: "書類が見つかりません",
  partnerRequired: "送付先を選択してください",
  partnerNotFound: "送付先が見つかりません",
  deliveryNotFound: "送付の記録が見つかりません",
  faxInvalid: "FAX 番号は数字とハイフンで 20 文字までです",
  bodyTooLong: "本文は 1,000 文字までです",
  remarksTooLong: "備考は 300 文字までです",
  linesTooMany: "手で足す行は 10 行までです",
  lineLabelRequired: "書類名を入力してください",
} as const;

/* ============================================================================
 * 型（API）
 * ========================================================================== */

const trimmed = (max: number, required?: string) =>
  z
    .string()
    .transform((s) => s.trim())
    .pipe(required ? z.string().min(1, required).max(max) : z.string().max(max));

/** テンプレート（GET） */
export const CoverLetterTemplate = z.object({
  id: z.string().uuid(),
  name: z.string(),
  titleFax: z.string(),
  titleWindow: z.string(),
  greetingFax: CoverGreeting,
  greetingWindow: CoverGreeting,
  body: z.string(),
  remarks: z.string(),
  printAddress: z.boolean(),
  printRepresentative: z.boolean(),
  printStaff: z.boolean(),
  printStaffEmail: z.boolean(),
  isDefault: z.boolean(),
  sortNo: z.number().int(),
  /** 版（#202。PUT に付けて送る） */
  updatedAt: z.string(),
});
export type CoverLetterTemplate = z.infer<typeof CoverLetterTemplate>;

/** テンプレートの作成・直し（省いた欄は変えない。直しは version 必須） */
export const CoverLetterTemplateInput = z.object({
  name: trimmed(COVER_LETTER_LIMITS.name, COVER_LETTER_MSG.nameRequired).optional(),
  titleFax: trimmed(COVER_LETTER_LIMITS.title, COVER_LETTER_MSG.titleRequired).optional(),
  titleWindow: trimmed(COVER_LETTER_LIMITS.title, COVER_LETTER_MSG.titleRequired).optional(),
  greetingFax: CoverGreeting.optional(),
  greetingWindow: CoverGreeting.optional(),
  body: z.string().max(COVER_LETTER_LIMITS.body, COVER_LETTER_MSG.bodyTooLong).optional(),
  remarks: z.string().max(COVER_LETTER_LIMITS.remarks, COVER_LETTER_MSG.remarksTooLong).optional(),
  printAddress: z.boolean().optional(),
  printRepresentative: z.boolean().optional(),
  printStaff: z.boolean().optional(),
  printStaffEmail: z.boolean().optional(),
  version: z.string().optional(),
});
export type CoverLetterTemplateInput = z.infer<typeof CoverLetterTemplateInput>;

/** 0.5mm 刻みか（浮動小数の誤差は見ない） */
const onStep = (v: number): boolean => Math.abs(v / COVER_ENVELOPE_RANGE.step - Math.round(v / COVER_ENVELOPE_RANGE.step)) < 1e-9;
/** 欄の値の誤り（範囲の外・刻み）。無ければ null。DB の fn_cover_letter_settings_ok と同じ決まり */
export function coverEnvelopeError(v: { kind: string; x: number; y: number; w: number; h: number; dx: number; dy: number }): string | null {
  const k = COVER_ENVELOPE_KINDS[v.kind as CoverEnvelopeKind];
  if (!k) return COVER_LETTER_MSG.envelopeOutside;
  const nums = [v.x, v.y, v.w, v.h, v.dx, v.dy];
  if (nums.some((n) => typeof n !== "number" || !Number.isFinite(n))) return COVER_LETTER_MSG.envelopeOutside;
  if (v.w < COVER_ENVELOPE_RANGE.w[0] || v.w > COVER_ENVELOPE_RANGE.w[1] || v.h < COVER_ENVELOPE_RANGE.h[0] || v.h > COVER_ENVELOPE_RANGE.h[1]) return COVER_LETTER_MSG.envelopeSize;
  if (v.x < 0 || v.y < 0 || v.x + v.w > k.w || v.y + v.h > k.h) return COVER_LETTER_MSG.envelopeOutside;
  if (Math.abs(v.dx) > COVER_ENVELOPE_RANGE.shift || Math.abs(v.dy) > COVER_ENVELOPE_RANGE.shift) return COVER_LETTER_MSG.envelopeShiftRange;
  if (!nums.every(onStep)) return COVER_LETTER_MSG.envelopeStep;
  return null;
}
/** 窓付封筒の設定（mm）：封筒の種類・窓の 左端から（x）・上端から（y）・幅（w）・高さ（h）・宛名の微調整 左右（dx、＋で右）・上下（dy、＋で下） */
export const CoverEnvelope = z
  .object({ kind: z.enum(["naga3"]), x: z.number(), y: z.number(), w: z.number(), h: z.number(), dx: z.number(), dy: z.number() })
  .superRefine((v, ctx) => {
    const e = coverEnvelopeError(v);
    if (e) ctx.addIssue({ code: "custom", message: e });
  });
export type CoverEnvelope = z.infer<typeof CoverEnvelope>;

/** 会社の設定（GET /cover-letters/settings）。printDefault＝印刷に添付・mailDefault＝メールに添付（#303 B1） */
export const CoverLetterSettings = z.object({ printDefault: z.boolean(), mailDefault: z.boolean(), envelope: CoverEnvelope, templates: z.array(CoverLetterTemplate) });
export type CoverLetterSettings = z.infer<typeof CoverLetterSettings>;
/** PUT /cover-letters/settings（省いたキーは変えない） */
export const CoverLetterSettingsUpdate = z.object({ printDefault: z.boolean().optional(), mailDefault: z.boolean().optional(), envelope: CoverEnvelope.optional() });

/**
 * 会社の送付状の既定（#303 B1）：tenants.cover_letter_settings の printDefault・mailDefault。省いたキー（NULL・前からの会社）は製品の既定（PRODUCTS[product].cover＝
 * 現場力 印刷オン・メールオフ／営業力 どちらもオフ）。API（loadSettings）と画面のテストが同じ関数を使う
 */
export function coverDefaultsOf(product: { print: boolean; mail: boolean }, stored: unknown): { printDefault: boolean; mailDefault: boolean } {
  const s = stored && typeof stored === "object" ? (stored as { printDefault?: unknown; mailDefault?: unknown }) : {};
  return {
    printDefault: typeof s.printDefault === "boolean" ? s.printDefault : product.print,
    mailDefault: typeof s.mailDefault === "boolean" ? s.mailDefault : product.mail,
  };
}

/**
 * 送信の画面で送付状を付けた形で始めるか（#303 B1・B2）。強い順：
 *   ① {customer}の「送付状」（添付する＝true／添付しない＝false）
 *   ② 会社の既定（印刷に添付・メールに添付）。印刷は{customer}の窓付封筒がオンでもオン（窓付封筒は送付状があるときだけ＝#223 ★8）
 * 出力のたびに外せる（スイッチ）。未登録の送付先は ① が無い（null）
 */
export function coverStartsOn(a: { via: "print" | "mail"; printDefault: boolean; mailDefault: boolean; partnerAttach: boolean | null; partnerWindow: boolean }): boolean {
  if (a.partnerAttach !== null) return a.partnerAttach;
  return a.via === "mail" ? a.mailDefault : a.printDefault || a.partnerWindow;
}
export type CoverLetterSettingsUpdate = z.infer<typeof CoverLetterSettingsUpdate>;

/** 記の手の行（図面・カタログなど）。枚数は FAX のとき「本状含め」に足す */
export const CoverManualLine = z.object({
  label: trimmed(COVER_LETTER_LIMITS.lineLabel, COVER_LETTER_MSG.lineLabelRequired),
  copies: z.number().int().min(1).max(COVER_LETTER_LIMITS.copies).default(1),
  pages: z.number().int().min(1).max(COVER_LETTER_LIMITS.pages).nullable().default(null),
});
export type CoverManualLine = z.infer<typeof CoverManualLine>;

/** 郵便番号：空か7桁（「530-0001」「〒５３０-０００１」も可。数字だけにして持つ） */
const zip7 = z
  .string()
  .trim()
  .transform((v) => v.normalize("NFKC").replace(/[^0-9]/g, ""))
  .pipe(z.string().regex(/^(\d{7})?$/, COVER_LETTER_MSG.recipientPostalInvalid));
/**
 * 未登録の送付先（3回目の前の直し＝★13、docs/89 §17.3）。送付先を {customer}／{vendor} から選ばずに、その場で入力する。
 * 会社名は送付状の中身（平文＝ルール5）、部署・担当者・郵便番号・住所は送付の記録の recipient_enc（暗号化）。FAX は送付状の中身の toFax（to_fax_enc）
 */
export const CoverRecipientInput = z.object({
  name: z.string().transform((s) => s.trim()).pipe(z.string().max(COVER_LETTER_LIMITS.recipientName)),
  department: trimmed(COVER_LETTER_LIMITS.recipientDepartment).default(""),
  contact: trimmed(COVER_LETTER_LIMITS.recipientContact).default(""),
  postalCode: zip7.default(""),
  address: z.string().max(COVER_LETTER_LIMITS.recipientAddress).default(""),
});
export type CoverRecipientInput = z.infer<typeof CoverRecipientInput>;

/** 送付状の中身（出力の枠で直せるもの）。省けばテンプレートと帳票から */
export const CoverLetterContent = z.object({
  templateId: z.string().uuid().nullable().optional(),
  mode: CoverMode.default("fax"),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  subject: z.string().max(COVER_LETTER_LIMITS.subject).optional(),
  title: trimmed(COVER_LETTER_LIMITS.title, COVER_LETTER_MSG.titleRequired).optional(),
  greeting: CoverGreeting.optional(),
  body: z.string().max(COVER_LETTER_LIMITS.body, COVER_LETTER_MSG.bodyTooLong).optional(),
  remarks: z.string().max(COVER_LETTER_LIMITS.remarks, COVER_LETTER_MSG.remarksTooLong).optional(),
  /** 送信先 FAX（FAX の型）。省けば 支店 → 会社 の FAX */
  toFax: z
    .string()
    .trim()
    .max(COVER_LETTER_LIMITS.fax, COVER_LETTER_MSG.faxInvalid)
    .transform((v) => formatPhone(v))
    .pipe(z.string().regex(/^[0-9-]*$/, COVER_LETTER_MSG.faxInvalid))
    .optional(),
  /** 送付先の担当者（選び直せる）。null＝会社宛て（御中）。省けば帳票の担当者 */
  contactId: z.string().uuid().nullable().optional(),
  manualLines: z.array(CoverManualLine).max(COVER_LETTER_LIMITS.manualLines, COVER_LETTER_MSG.linesTooMany).default([]),
  /** 未登録の送付先（target.unregistered のときだけ使う。★13）。印刷・保存（/deliveries）では会社名が要る（API が見る） */
  recipient: CoverRecipientInput.optional(),
});
export type CoverLetterContent = z.infer<typeof CoverLetterContent>;
export type CoverLetterContentInput = z.input<typeof CoverLetterContent>;

/** 同封書類の書類名の上書き（受け取った書類・その場の PDF。記の行と通し番号に出る） */
const itemLabel = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1, COVER_LETTER_MSG.lineLabelRequired).max(COVER_LETTER_LIMITS.lineLabel))
  .optional();
/**
 * 同封書類：出した帳票（見積書・請求書・領収書。form＝内訳のある紙の形）・見積依頼書・受け取った書類（received。2回目）・その場で選ぶ PDF（file＝POST /cover-letters/uploads の id。2回目）。
 * 並びは配列の順（記の行・PDF のページも同じ順）
 */
export const DeliveryItemRef = z.discriminatedUnion("kind", [
  z.object({ kind: z.enum(["quote", "invoice", "receipt"]), id: z.string().uuid(), form: z.enum(["sheets", "flat"]).optional() }),
  z.object({ kind: z.literal("rfq"), id: z.string().uuid() }),
  // #233：出した{order}（送付状・メール送付の同封書類）。terms＝印刷・FAX で約款を別紙で同封するか（2c、docs/93 §14.2。省けば会社の「印刷で約款」。メールでは見ない＝約款は PDF の後ろのまま）
  // changes＝訂正版（2回目から）の最後に「変更点」のページを付けるか（#233 2b、docs/93 §14.5。省けば付けない＝1回目と同じ紙）
  z.object({ kind: z.literal("order"), id: z.string().uuid(), terms: z.boolean().optional(), changes: z.boolean().optional() }),
  // #233 3b：出した{paymentNotice}（送付状・メール送付の同封書類。メールのボタンは確認ページ）
  z.object({ kind: z.literal("payment_notice"), id: z.string().uuid() }),
  // #266：出した{deliveryNote}（送付状・メール送付の同封書類）
  z.object({ kind: z.literal("delivery_note"), id: z.string().uuid() }),
  // #266 1b：出した{acceptance}
  z.object({ kind: z.literal("acceptance"), id: z.string().uuid() }),
  z.object({ kind: z.literal("received"), id: z.string().uuid(), label: itemLabel }),
  z.object({ kind: z.literal("file"), id: z.string().uuid(), label: itemLabel }),
]);
export type DeliveryItemRef = z.infer<typeof DeliveryItemRef>;
export type DeliveryItemKind = DeliveryItemRef["kind"];
/** 送付先を持つ書類（帳票・見積依頼書）。受け取った書類・その場の PDF は送付先を持たない＝どの送付にも入れられる */
export const isAddressedItem = (r: { kind: DeliveryItemKind }): boolean => r.kind !== "received" && r.kind !== "file";

/**
 * 同封書類の鍵（URL の items=・画面の行の key）：「quote.<id>」「quote.<id>.flat」「received.<id>」「order.<id>.terms」（約款を別紙で）「order.<id>.noterms」（付けない）・
 * 訂正版の「変更点」のページを付ける{order}は後ろに「.changes」（#233 2b。例「order.<id>.terms.changes」）
 */
export const coverItemKey = (r: { kind: DeliveryItemKind; id: string; form?: "sheets" | "flat" | undefined; terms?: boolean | undefined; changes?: boolean | undefined }): string =>
  `${r.kind}.${r.id}${r.form === "flat" ? ".flat" : ""}${r.kind === "order" && r.terms !== undefined ? (r.terms ? ".terms" : ".noterms") : ""}${r.kind === "order" && r.changes ? ".changes" : ""}`;
/** 鍵から同封書類に（形が違えば null） */
export function parseCoverItemKey(key: string): DeliveryItemRef | null {
  const [kind, id, form, ...more] = key.split(".");
  const flags = [form, ...more];
  const r = DeliveryItemRef.safeParse(
    kind === "quote" || kind === "invoice" || kind === "receipt"
      ? { kind, id, ...(form === "flat" ? { form } : {}) }
      : kind === "order"
        ? { kind, id, ...(flags.includes("terms") ? { terms: true } : flags.includes("noterms") ? { terms: false } : {}), ...(flags.includes("changes") ? { changes: true } : {}) }
        : { kind, id },
  );
  return r.success ? r.data : null;
}

/**
 * 同じ送付先（docs/89 §6・§15 の13）：取引先と支店が同じ＋送信先 FAX 番号が同じ。担当者は送付状で1人選ぶので見ない。
 * FAX 番号は支店（無ければ{customer}）に1つなので、いまは取引先と支店が同じなら同じ番号になる（番号は数字だけでくらべる）
 */
export function sameRecipient(a: { partnerId: string; branchId: string | null; fax: string }, b: { partnerId: string; branchId: string | null; fax: string }): boolean {
  const digits = (v: string) => v.replace(/[^0-9]/g, "");
  return a.partnerId === b.partnerId && (a.branchId ?? null) === (b.branchId ?? null) && digits(a.fax) === digits(b.fax);
}

/** 束ねの上限の断り（無ければ null）。画面（朱の1行）と API（400 COVER_BUNDLE_LIMIT）が同じ関数 */
export function bundleLimitError(v: { items: number; pages: number; bytes: number }): string | null {
  const L = COVER_BUNDLE_LIMITS;
  if (v.items > L.items) return COVER_LETTER_MSG.bundleTooMany.replace("{n}", String(L.items));
  if (v.pages > L.pages) return COVER_LETTER_MSG.bundleTooManyPages.replace("{n}", String(L.pages)).replace("{m}", String(v.pages));
  if (v.bytes > L.bytes) {
    const mb = (b: number) => String(Math.ceil((b / 1024 / 1024) * 10) / 10);
    return COVER_LETTER_MSG.bundleTooBig.replace("{n}", String(L.bytes / 1024 / 1024)).replace("{m}", mb(v.bytes));
  }
  return null;
}

/** 送付先と同封書類。送付先を持つ書類があれば送付先はその書類から（partnerId・branchId は単体送付状・受け取った書類だけの送付） */
export const DeliveryTarget = z.object({
  items: z
    .array(DeliveryItemRef)
    .max(COVER_BUNDLE_LIMITS.items, COVER_LETTER_MSG.bundleTooMany.replace("{n}", String(COVER_BUNDLE_LIMITS.items)))
    .default([])
    .refine((xs) => new Set(xs.map((x) => `${x.kind}.${x.id}`)).size === xs.length, COVER_LETTER_MSG.itemDup),
  partnerId: z.string().uuid().optional(),
  branchId: z.string().uuid().nullable().optional(),
  groupId: z.string().uuid().nullable().optional(),
  /**
   * 未登録の送付先（3回目の前の直し＝★13）：取引先を選ばず、送付状の中身の recipient（会社名・部署・担当者・〒・住所）と送信先 FAX で送る。
   * 帳票・見積依頼書は同封できない（400 COVER_UNREGISTERED_DOC。受け取った書類・その場の PDF だけ）
   */
  unregistered: z.boolean().optional(),
});
export type DeliveryTarget = z.infer<typeof DeliveryTarget>;
export type DeliveryTargetInput = z.input<typeof DeliveryTarget>;

/**
 * POST /cover-letters/draft：出力の枠の初めの値（テンプレート・送付先・記の自動の行・送信先 FAX）。
 * draftRfq＝送る前の見積依頼書も受ける（「送付」の確認の窓で送付状を直すため。番号は送る瞬間に採るので記の行は番号なし）。印刷・保存（/deliveries）は受けない
 */
export const CoverDraftRequest = z.object({ target: DeliveryTarget, draftRfq: z.boolean().default(false) });
/** 同封書類の行（2回目） */
export const CoverDraftItem = z.object({
  key: z.string(),
  kind: z.enum(["quote", "invoice", "receipt", "rfq", "order", "received", "file"]),
  label: z.string(),
  docNo: z.string().nullable(),
  pages: z.number().int(),
  bytes: z.number().int(),
  /** 同じ PDF の中の別の紙（見積依頼書の写真台帳）＝記の行が増える */
  extraLines: z.array(z.string()),
  /** 書類名を変えられる（受け取った書類・その場の PDF） */
  editable: z.boolean(),
  /** {order}の約款の別紙（#233 2c）：true＝同封する／false＝しない／null＝別紙にできない（仕入・基本契約書の記録あり・約款なし・{order}でない） */
  terms: z.boolean().nullable().default(null),
});
export type CoverDraftItem = z.infer<typeof CoverDraftItem>;
export const CoverAutoLine = z.object({ label: z.string(), docNo: z.string().nullable(), copies: z.number().int(), pages: z.number().int().nullable() });
export type CoverAutoLine = z.infer<typeof CoverAutoLine>;
export const CoverDraft = z.object({
  templates: z.array(CoverLetterTemplate),
  /** 既定のテンプレート */
  templateId: z.string().uuid(),
  /** 会社の「印刷に添付」（#303 B1。省いた会社は製品の既定） */
  printDefault: z.boolean(),
  /** 会社の「メールに添付」（#303 B1） */
  mailDefault: z.boolean(),
  /** {customer}の「送付状」（#303 B2）：true＝添付する／false＝添付しない／null＝会社に従う（未登録の送付先も null） */
  partnerAttach: z.boolean().nullable(),
  /** {customer}の送信方法の既定（#303 B2）：発行したあとの送信の画面の初めの方法。未登録の送付先は print */
  sendMethod: z.enum(["print", "mail"]),
  /** 送付先。id が null＝未登録の送付先（★13。名前は送付状の中身の recipient から） */
  partner: z.object({ id: z.string().uuid().nullable(), name: z.string(), shortName: z.string() }),
  branch: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** 選べる担当者（その取引先の。支店があればその支店の担当者を先に） */
  contacts: z.array(z.object({ id: z.string().uuid(), label: z.string() })),
  contactId: z.string().uuid().nullable(),
  /** 支店 → 会社 の FAX（無ければ空） */
  toFax: z.string(),
  subject: z.string(),
  date: z.string(),
  autoLines: z.array(CoverAutoLine),
  /** 同封書類の PDF のページ数の合計（送付状を除く） */
  docPages: z.number().int(),
  /** （2回目）同封書類の行（並びは target の順）。束ねの画面の 上へ・下へ・削除・書類名の欄 */
  items: z.array(CoverDraftItem),
  /** （2回目）同封書類の大きさの合計（バイト。上限 COVER_BUNDLE_LIMITS.bytes） */
  bytes: z.number().int(),
  /** （2回目）その{customer}の支店・営業所（単体送付状で選ぶ） */
  branches: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** （2回目）窓付封筒で始めるか（{customer}ごとの既定。テンプレートの既定は templateId に入っている） */
  defaultWindow: z.boolean(),
});
export type CoverDraft = z.infer<typeof CoverDraft>;

/**
 * POST /cover-letters/candidates（2回目）：束ねの「書類追加」の候補。target＝いまの送付（送付先はその書類か partnerId・branchId から）。
 * groupId＝受け取った書類を見る{project}（省けば関係する{project}の1つ目）
 */
export const CoverCandidatesRequest = z.object({ target: DeliveryTarget, groupId: z.string().uuid().nullable().optional() });
export type CoverCandidatesRequestInput = z.input<typeof CoverCandidatesRequest>;
export const CoverCandidates = z.object({
  recipient: z.object({ partnerId: z.string().uuid(), partnerName: z.string(), branchId: z.string().uuid().nullable(), branchName: z.string().nullable(), fax: z.string() }),
  /** 同じ送付先の出した帳票・見積依頼書（{project}をまたぐ。新しい順・200 まで） */
  documents: z.array(
    z.object({
      kind: z.enum(["quote", "invoice", "receipt", "rfq"]),
      id: z.string().uuid(),
      label: z.string(),
      docNo: z.string().nullable(),
      subject: z.string(),
      groupName: z.string().nullable(),
      date: z.string().nullable(),
      included: z.boolean(),
    }),
  ),
  /** 受け取った書類を見る{project}の選択肢（送付先の{project}と、同封書類の{project}） */
  groups: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  groupId: z.string().uuid().nullable(),
  /** その{project}の受け取った書類（PDF・画像。文字の書類は入らない） */
  received: z.array(z.object({ id: z.string().uuid(), title: z.string(), kindLabel: z.string(), format: z.enum(["pdf", "image"]), pages: z.number().int().nullable(), included: z.boolean() })),
});
export type CoverCandidates = z.infer<typeof CoverCandidates>;
/** POST /cover-letters/uploads の答え（その場で選んだ PDF。id は送付の file の id） */
export const CoverUpload = z.object({ id: z.string().uuid(), title: z.string(), pages: z.number().int(), bytes: z.number().int() });
export type CoverUpload = z.infer<typeof CoverUpload>;
/** POST /cover-letters/item-pdf：同封書類1つの PDF（束ねで入るのと同じバイト。束ねの画面の紙に並べる） */
export const CoverItemPdfRequest = z.object({ item: DeliveryItemRef });

/** POST /cover-letters/preview：送付状だけの PDF（記録しない）。答えのヘッダー x-gb-total-pages に「本状含め」の n */
export const CoverPreviewRequest = z.object({
  target: DeliveryTarget,
  cover: CoverLetterContent,
  draftRfq: z.boolean().default(false),
  /**
   * 見本の透かし（記録しない見本だけ）：draft＝「下書き」（見積依頼書の「PDF で確認（透かし入り）」）・confirm＝「確認用」（見積依頼の送付の画面。★16）。
   * 印刷・保存（/deliveries）の PDF には付かない
   */
  watermark: z.enum(["draft", "confirm"]).optional(),
  /** メールの送付状（#225、docs/91 §4）：窓付封筒の型の紙で、折り目の印と FAX の通し番号を刷らない */
  mail: z.boolean().optional(),
});
export type CoverPreviewRequest = z.infer<typeof CoverPreviewRequest>;
export type CoverPreviewRequestInput = z.input<typeof CoverPreviewRequest>;
export const COVER_TOTAL_PAGES_HEADER = "x-gb-total-pages";
/** 見本の答えのヘッダー：出す PDF のページ数（送付状＋同封書類。手の行の枚数は入らない）＝束ねの上限（本状含め 50 ページ）を画面で見る（2回目） */
export const COVER_PDF_PAGES_HEADER = "x-gb-pdf-pages";
/** 窓付封筒の見本（POST /cover-letters/preview・設定の見本）の答えのヘッダー：宛名を 9pt まで縮めても枠に入らなければ "long"（見本の下に COVER_LETTER_MSG.addressTooLong） */
export const COVER_ADDRESS_HEADER = "x-gb-cover-address";

/** POST /deliveries：印刷・PDF の保存。送付状＋同封書類の PDF 1本を作って保存し、記録する */
export const DeliveryCreate = z.object({
  method: z.enum(["print", "download"]),
  target: DeliveryTarget,
  cover: CoverLetterContent,
  /** その場で打った FAX 番号を{customer}に登録する（既定オフ＝送付の記録にだけ残す。docs/89 §15 の6） */
  saveFax: z.boolean().default(false),
});
export type DeliveryCreate = z.infer<typeof DeliveryCreate>;
export type DeliveryCreateInput = z.input<typeof DeliveryCreate>;
export const DeliveryResult = z.object({ id: z.string().uuid(), pageCount: z.number().int(), pdfUrl: z.string(), fileName: z.string() });
export type DeliveryResult = z.infer<typeof DeliveryResult>;

/**
 * メールの行の状態（#225、docs/91 §3.4）。queued＝送信中・sent＝送信済（添付）・unopened／opened＝未取得／取得済（閲覧ページ）・
 * bounced＝届いていません（不達の知らせ）・revoked＝閲覧ページを無効にした・expired＝公開期限が過ぎた（取得なし）・failed＝送信失敗
 */
export const DeliveryMailState = z.enum(["queued", "sent", "unopened", "opened", "bounced", "revoked", "expired", "failed"]);
export type DeliveryMailState = z.infer<typeof DeliveryMailState>;
/** 一覧のメールの行（#225）。attention＝要確認（届いていません・送信失敗・未取得 7日。確認済みは除く） */
export const DeliveryMail = z.object({
  /** page＝閲覧ページ・attach＝添付・ack＝電子承諾の{order}の承諾ページ（閲覧ページを作らない。DB は mail_mode page で閲覧ページの行が無いもの。#233 §13.7 イ） */
  mode: z.enum(["page", "attach", "ack"]),
  state: DeliveryMailState,
  /** 宛先（To。「, 」区切り） */
  toText: z.string(),
  firstAccessAt: z.string().nullable(),
  accessCount: z.number().int(),
  expiresAt: z.string().nullable(),
  attention: z.boolean(),
  dismissed: z.boolean(),
});
export type DeliveryMail = z.infer<typeof DeliveryMail>;

/**
 * 送付の記録の一覧（GET /deliveries?document=|rfq=|partner=|group=、2回目）。新しい順・200 まで。
 * 記録は書き換えない（開くと保存した PDF＝GET /deliveries/:id/pdf）
 */
export const DeliverySummary = z.object({
  id: z.string().uuid(),
  at: z.string(),
  method: z.enum(["print", "download", "email", "fax", "onnet", "link", "manual"]),
  mode: CoverMode.nullable(),
  /** 手で切り替えた送付（#304）：sent＝送付済にした／unsent＝未送付に戻した。ほかの記録は null */
  manual: z.enum(["sent", "unsent"]).nullable().optional(),
  /** リンクを送った経路（#317）：copy＝リンクをコピー／line＝LINE／sms＝SMS。リンクでない記録は null */
  linkVia: z.enum(["copy", "line", "sms"]).nullable().optional(),
  /** 送付先の名前：取引先があれば略称（空なら「株式会社」などを外した名前）、無ければ送付状の中身の会社名 */
  partnerName: z.string().nullable(),
  /** 正式名称（#313：一覧で乗せると出す。取引先の無い記録は partnerName と同じ） */
  partnerFullName: z.string().nullable().optional(),
  branchName: z.string().nullable(),
  /** PDF のページ数（本状含め） */
  pageCount: z.number().int().nullable(),
  /** 同封書類（記の行。手の行も） */
  items: z.array(z.object({ label: z.string(), docNo: z.string().nullable() })),
  /** 送付者の氏名 */
  by: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  /** 未登録の送付先（取引先の無い記録。★13）。名前は partnerName（送付状の中身の会社名） */
  unregistered: z.boolean(),
  /** メールの行（#225）。印刷・PDF の保存の行は null */
  mail: DeliveryMail.nullable().optional(),
});
export type DeliverySummary = z.infer<typeof DeliverySummary>;
/** next＝続きの鍵（会社の全件の一覧だけ。GET /deliveries?before=<next>。無ければ null） */
export const DeliveryList = z.object({ items: z.array(DeliverySummary), next: z.string().nullable().optional() });
/** 記録の画面のメールの節（#225、docs/91 §3.4）：宛先・件名・本文・無効にした日時・不達・送ったファイル・取得の記録 */
export const DeliveryMailDetail = z.object({
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  bccSelf: z.boolean(),
  subject: z.string(),
  body: z.string(),
  revokedAt: z.string().nullable(),
  revokeReason: z.string().nullable(),
  bouncedAt: z.string().nullable(),
  bounceCode: z.string().nullable(),
  error: z.string().nullable(),
  files: z.array(z.object({ no: z.number().int(), label: z.string(), docNo: z.string().nullable(), fileName: z.string(), bytes: z.number().int(), contentType: z.string() })),
  accesses: z.array(z.object({ at: z.string(), action: z.enum(["open", "view", "download", "zip"]), fileNo: z.number().int().nullable(), counted: z.boolean() })),
});
export type DeliveryMailDetail = z.infer<typeof DeliveryMailDetail>;
/** GET /deliveries/:id：送付の記録1件（PDF の画面の上の帯。★18）。fileName＝ダウンロードの名前。mailDetail＝メールの行だけ（#225） */
export const DeliveryDetail = DeliverySummary.extend({ fileName: z.string(), mailDetail: DeliveryMailDetail.nullable().optional() });
export type DeliveryDetail = z.infer<typeof DeliveryDetail>;
export type DeliveryList = z.infer<typeof DeliveryList>;
/**
 * 一覧の「送付方法」の札（#313・#317）。method で必ず決める（「—」は出さない）：
 *   手動／メール／印刷（送付状ありなら型＝窓付・FAX）／PDF 保存／リンク（経路が LINE・SMS ならその名前）／FAX（fax・onnet）
 */
export const deliveryModeText = (d: { method: string; mode: CoverMode | null; linkVia?: "copy" | "line" | "sms" | null | undefined }): string => {
  const M = COVER_LETTER_MSG;
  switch (d.method) {
    case "manual":
      return M.modeManual;
    case "email":
      return M.modeMail;
    case "download":
      return M.modeDownload;
    case "link":
      return d.linkVia === "line" ? M.modeLine : d.linkVia === "sms" ? M.modeSms : M.modeLink;
    case "fax":
    case "onnet":
      return M.modeFax;
    default:
      return d.mode === "window" ? M.modeWindow : d.mode === "fax" ? M.modeFax : M.modePrint;
  }
};
/** 手動の記録の右の札（#313）：送付済／未送付に戻す。手動でない記録は null */
export const deliveryManualText = (d: { method: string; manual?: "sent" | "unsent" | null | undefined }): string | null =>
  d.method !== "manual" ? null : d.manual === "unsent" ? COVER_LETTER_MSG.manualUnsent : COVER_LETTER_MSG.manualSent;
